import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useEmailVerification } from "@domain/auth/hooks";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { Button } from "@ui/components/buttons/button";
import { gap } from "@ui/styles/utils";
import { OnboardingDisconnect } from "./common";

interface EmailVerifyProps extends React.HTMLAttributes<HTMLDivElement> {
	profile?: CurrentProfile | null;
}

export const EmailVerify: React.FC<EmailVerifyProps> = ({ profile, ...htmlProps }) => {
	const { sendEmail, loading, sent } = useEmailVerification();
	const { authService } = useServices();
	const firebaseUser = useObservable(authService.firebaseUser);
	const interval = useRef<NodeJS.Timeout>(null);
	const email = profile?.email || firebaseUser?.email || "";

	useEffect(() => {
		interval.current = setInterval(async () => {
			await authService.checkUserVerified();
		}, 4000);

		return () => {
			if (interval.current) {
				clearInterval(interval.current);
			}
		};
	}, [authService]);

	return (
		<Container {...htmlProps}>
			<Title>{I18n.t("auth.verify.title")}</Title>
			<Lead>{I18n.t("auth.verify.lead", { email })}</Lead>
			<Button variant="primary" size="lg" block disabled={sent} loading={loading} onClick={sendEmail}>
				{I18n.t("auth.verify.retry")}
			</Button>
			<OnboardingDisconnect />
		</Container>
	);
};

const Container = styled.div`
	${gap(20)};
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

const Title = styled.h1`
	margin: 0 0 6px;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	line-height: 1.2;
	color: #13131a;
`;

const Lead = styled.p`
	margin: 0;
	color: #8a94a4;
	font-size: 15px;
	line-height: 1.5;
`;
